import { useState, useEffect } from "react"
import { Search, FileText, FolderOpen, ChevronRight, ChevronDown, Library } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { FileTypeIcon } from "@/components/ui/file-type-icon"
import { useAssistantStore } from "@/stores/assistant-store"
import { useLibraryStore } from "@/stores/library-store"
import { useBidsStore } from "@/stores/bids-store"
import { api } from "@/lib/api"
import type { Document } from "@/lib/schemas"

interface ContextSelectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface SelectedDocument {
  id: string
  name: string
  type: "library" | "bid"
  bidId?: string
  folderId?: string
}

export function ContextSelectDialog({ open, onOpenChange }: ContextSelectDialogProps) {
  const { context, setContext, grantBidId: storeGrantBidId, setGrantBidId } = useAssistantStore()
  const {
    documents: libraryDocuments,
    folders,
    fetchDocuments: fetchLibraryDocs,
    fetchFolders,
    getChildFolders,
    getRootFolders,
  } = useLibraryStore()
  const { bids, fetchBids } = useBidsStore()

  const [searchQuery, setSearchQuery] = useState("")
  const [selectedDocs, setSelectedDocs] = useState<SelectedDocument[]>(
    context.selectedDocuments ?? []
  )
  const [selectAllLibrary, setSelectAllLibrary] = useState(context.allLibrary ?? true)
  const [activeTab, setActiveTab] = useState<"library" | "bids">("library")

  // The single bid currently in Sources (grant). One bid at a time — selecting
  // docs from a bid (or "All documents in this bid") sets this; other bids
  // disable until it's cleared. Derived and applied together with selectedDocs.
  const [grantBidId, setLocalGrantBidId] = useState<string | null>(storeGrantBidId)

  // Folder expansion state
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

  // Bid expansion state and documents
  const [expandedBids, setExpandedBids] = useState<Set<string>>(new Set())
  const [bidDocuments, setBidDocuments] = useState<Record<string, Document[]>>({})
  const [loadingBidDocs, setLoadingBidDocs] = useState<Set<string>>(new Set())

  // Fetch data when dialog opens
  useEffect(() => {
    if (open) {
      void fetchLibraryDocs()
      void fetchFolders()
      void fetchBids()
    }
  }, [open, fetchLibraryDocs, fetchFolders, fetchBids])

  // Auto-load all bid documents when dialog opens and bids are available
  useEffect(() => {
    if (open && bids.length > 0) {
      bids.forEach((bid) => {
        if (!bidDocuments[bid.id] && !loadingBidDocs.has(bid.id)) {
          void loadBidDocuments(bid.id)
        }
      })
    }
  }, [open, bids])

  // Reset selection state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDocs(context.selectedDocuments ?? [])
      setSelectAllLibrary(context.allLibrary ?? true)
      setLocalGrantBidId(storeGrantBidId)
    }
  }, [open, context.selectedDocuments, context.allLibrary, storeGrantBidId])

  // Filter documents/folders by search
  const filteredDocs = libraryDocuments.filter((doc) =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredBids = bids.filter((bid) =>
    bid.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Selection helpers
  const isDocSelected = (docId: string) => selectedDocs.some((d) => d.id === docId)

  const toggleDocument = (doc: { id: string; name: string; folderId?: string | null }, type: "library" | "bid", bidId?: string) => {
    // When selecting specific docs, disable "select all"
    if (type === "library" && selectAllLibrary) {
      setSelectAllLibrary(false)
    }

    if (isDocSelected(doc.id)) {
      setSelectedDocs(selectedDocs.filter((d) => d.id !== doc.id))
    } else {
      const newDoc: SelectedDocument = {
        id: doc.id,
        name: doc.name,
        type,
      }
      if (type === "library" && doc.folderId) {
        newDoc.folderId = doc.folderId
      }
      if (type === "bid" && bidId) {
        newDoc.bidId = bidId
      }
      setSelectedDocs([...selectedDocs, newDoc])
    }
  }

  // Folder helpers
  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId)
    } else {
      newExpanded.add(folderId)
    }
    setExpandedFolders(newExpanded)
  }

  const isFolderExpanded = (folderId: string) => expandedFolders.has(folderId)

  const getDocsInFolder = (folderId: string | null) => {
    return filteredDocs.filter((doc) => doc.folderId === folderId)
  }

  const getDocCountInFolder = (folderId: string): number => {
    const directDocs = libraryDocuments.filter((doc) => doc.folderId === folderId).length
    const childFolders = getChildFolders(folderId)
    const childDocs = childFolders.reduce((sum, folder) => sum + getDocCountInFolder(folder.id), 0)
    return directDocs + childDocs
  }

  const selectAllInFolder = (folderId: string, selected: boolean) => {
    const docsInFolder = libraryDocuments.filter((doc) => doc.folderId === folderId)
    if (selected) {
      // Add all docs from this folder
      const newDocs: SelectedDocument[] = docsInFolder.filter((doc) => !isDocSelected(doc.id)).map((doc) => {
        const base: SelectedDocument = {
          id: doc.id,
          name: doc.name,
          type: "library" as const,
        }
        if (doc.folderId) {
          base.folderId = doc.folderId
        }
        return base
      })
      setSelectedDocs([...selectedDocs, ...newDocs])
    } else {
      // Remove all docs from this folder
      setSelectedDocs(selectedDocs.filter((d) => d.folderId !== folderId))
    }
    setSelectAllLibrary(false)
  }

  const isFolderFullySelected = (folderId: string) => {
    const docsInFolder = libraryDocuments.filter((doc) => doc.folderId === folderId)
    return docsInFolder.length > 0 && docsInFolder.every((doc) => isDocSelected(doc.id))
  }

  const isFolderPartiallySelected = (folderId: string) => {
    const docsInFolder = libraryDocuments.filter((doc) => doc.folderId === folderId)
    const selectedCount = docsInFolder.filter((doc) => isDocSelected(doc.id)).length
    return selectedCount > 0 && selectedCount < docsInFolder.length
  }

  // Bid helpers
  const toggleBid = (bidId: string) => {
    const newExpanded = new Set(expandedBids)
    if (newExpanded.has(bidId)) {
      newExpanded.delete(bidId)
    } else {
      newExpanded.add(bidId)
    }
    setExpandedBids(newExpanded)
  }

  const loadBidDocuments = async (bidId: string) => {
    setLoadingBidDocs((prev) => new Set([...prev, bidId]))
    try {
      const docs = await api.get<Document[]>(`/bids/${bidId}/documents`)
      setBidDocuments((prev) => ({ ...prev, [bidId]: docs }))
    } catch (error) {
      console.error("Failed to load bid documents:", error)
      setBidDocuments((prev) => ({ ...prev, [bidId]: [] }))
    } finally {
      setLoadingBidDocs((prev) => {
        const next = new Set(prev)
        next.delete(bidId)
        return next
      })
    }
  }

  // Grant helpers (one bid in Sources at a time)
  const bidHasSpecificDocs = (bidId: string) =>
    selectedDocs.some((d) => d.type === "bid" && d.bidId === bidId)

  // Whole-bid grant = this bid is granted with NO doc filter (search the whole
  // bid via search_evidence). Distinct from selecting every individual doc.
  const isWholeBidGranted = (bidId: string) =>
    grantBidId === bidId && !bidHasSpecificDocs(bidId)

  // A bid is locked out while another bid holds the grant.
  const isBidDisabled = (bidId: string) => grantBidId !== null && grantBidId !== bidId

  // Toggle the per-bid "All documents in this bid" option.
  const toggleWholeBid = (bidId: string, checked: boolean) => {
    if (isBidDisabled(bidId)) return
    // Drop any specific-doc selections for this bid either way.
    const withoutBidDocs = selectedDocs.filter((d) => !(d.type === "bid" && d.bidId === bidId))
    setSelectedDocs(withoutBidDocs)
    setLocalGrantBidId(checked ? bidId : null)
  }

  // Toggle a single bid document. Selecting sets/keeps the grant; clearing the
  // last selection for the bid clears the grant.
  const toggleBidDocument = (doc: { id: string; name: string }, bidId: string) => {
    if (isBidDisabled(bidId)) return
    if (isDocSelected(doc.id)) {
      const next = selectedDocs.filter((d) => d.id !== doc.id)
      setSelectedDocs(next)
      // Clearing a bid's last selection clears the grant.
      if (!next.some((d) => d.type === "bid" && d.bidId === bidId)) {
        setLocalGrantBidId(null)
      }
    } else {
      setSelectedDocs([...selectedDocs, { id: doc.id, name: doc.name, type: "bid", bidId }])
      setLocalGrantBidId(bidId)
    }
  }

  // Select all handlers
  const handleSelectAllLibrary = (checked: boolean) => {
    setSelectAllLibrary(checked)
    if (checked) {
      // Clear specific library selections when selecting all
      setSelectedDocs(selectedDocs.filter((d) => d.type !== "library"))
    }
  }

  const handleApply = () => {
    setGrantBidId(grantBidId)
    setContext({
      ...context,
      factBankOnly: false,
      selectedDocuments: selectedDocs,
      allLibrary: selectAllLibrary,
    })
    onOpenChange(false)
  }

  const handleCancel = () => {
    setSelectedDocs(context.selectedDocuments ?? [])
    setSelectAllLibrary(context.allLibrary ?? true)
    setLocalGrantBidId(storeGrantBidId)
    onOpenChange(false)
  }

  // Get unfiled documents (no folder)
  const unfiledDocs = filteredDocs.filter((doc) => !doc.folderId)

  // Get selection summary text
  const getSelectionSummary = () => {
    const libraryCount = selectAllLibrary ? libraryDocuments.length : selectedDocs.filter((d) => d.type === "library").length
    const bidCount = selectedDocs.filter((d) => d.type === "bid").length
    const parts: string[] = []
    if (selectAllLibrary && bidCount === 0 && !grantBidId) {
      return "All Library Documents"
    }
    if (libraryCount > 0) {
      parts.push(`${libraryCount} library doc${libraryCount !== 1 ? "s" : ""}`)
    }
    if (grantBidId) {
      const grantedBid = bids.find((b) => b.id === grantBidId)
      const bidTitle = grantedBid?.title ?? "bid"
      parts.push(
        bidCount > 0
          ? `${bidCount} doc${bidCount !== 1 ? "s" : ""} from ${bidTitle}`
          : `whole bid: ${bidTitle}`
      )
    }
    return parts.length > 0 ? parts.join(", ") : "No documents selected"
  }

  // Render folder tree recursively
  const renderFolderTree = (parentId: string | null, depth: number = 0) => {
    const childFolders = parentId === null ? getRootFolders() : getChildFolders(parentId)
    const folderList = searchQuery ? childFolders.filter((f) => filteredFolders.includes(f)) : childFolders

    return folderList.map((folder) => {
      const isExpanded = isFolderExpanded(folder.id)
      const docCount = getDocCountInFolder(folder.id)
      const docsInFolder = getDocsInFolder(folder.id)
      const hasChildren = getChildFolders(folder.id).length > 0 || docsInFolder.length > 0
      const isFullySelected = isFolderFullySelected(folder.id)
      const isPartiallySelected = isFolderPartiallySelected(folder.id)

      return (
        <div key={folder.id}>
          {/* Folder row */}
          <div
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer overflow-hidden",
              depth > 0 && "ml-4"
            )}
          >
            <button
              className="p-0.5 hover:bg-muted rounded flex-shrink-0"
              onClick={() => toggleFolder(folder.id)}
            >
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )
              ) : (
                <div className="w-4" />
              )}
            </button>
            <Checkbox
              checked={isFullySelected}
              ref={(el) => {
                if (el && isPartiallySelected) {
                  el.dataset["state"] = "indeterminate"
                }
              }}
              onCheckedChange={(checked) => selectAllInFolder(folder.id, checked === true)}
              className="pointer-events-auto flex-shrink-0"
            />
            <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm flex-1 truncate min-w-0">{folder.name}</span>
            <span className="text-meta flex-shrink-0">({docCount})</span>
          </div>

          {/* Expanded content */}
          {isExpanded && (
            <div className="ml-4 overflow-hidden">
              {/* Child folders */}
              {renderFolderTree(folder.id, depth + 1)}

              {/* Documents in this folder */}
              {docsInFolder.map((doc) => (
                <button
                  key={doc.id}
                  className={cn(
                    "w-full min-w-0 flex items-center gap-2 px-2 py-1.5 ml-4 rounded-md text-left transition-colors overflow-hidden",
                    isDocSelected(doc.id)
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-accent"
                  )}
                  onClick={() => toggleDocument({ id: doc.id, name: doc.name, folderId: doc.folderId ?? null }, "library")}
                >
                  <div className="w-4 flex-shrink-0" />
                  <Checkbox
                    checked={isDocSelected(doc.id)}
                    className="pointer-events-none flex-shrink-0"
                  />
                  <FileTypeIcon mimeType={doc.mimeType} fileName={doc.name} size={20} className="flex-shrink-0" />
                  <span className="text-sm truncate min-w-0">{doc.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Choose documents</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="w-full">
            <TabsTrigger value="library" className="flex-1">Library</TabsTrigger>
            <TabsTrigger value="bids" className="flex-1">Bid Documents</TabsTrigger>
          </TabsList>

          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={activeTab === "library" ? "Search documents..." : "Search bids..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Library Tab */}
          <TabsContent value="library" className="mt-4 flex-1 min-h-0 flex flex-col">
            {/* Select All Library */}
            <div className="flex items-center gap-2 px-2 py-2 border-b border-border mb-2">
              <Checkbox
                checked={selectAllLibrary}
                onCheckedChange={handleSelectAllLibrary}
              />
              <Library className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Select All Library Documents</span>
            </div>

            <ScrollArea className="flex-1 min-h-0 w-full">
              {/* Folder tree */}
              {renderFolderTree(null)}

              {/* Unfiled documents */}
              {unfiledDocs.length > 0 && (
                <div className="mt-2">
                  {unfiledDocs.map((doc) => (
                    <button
                      key={doc.id}
                      className={cn(
                        "w-full min-w-0 flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors overflow-hidden",
                        isDocSelected(doc.id)
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-accent"
                      )}
                      onClick={() => toggleDocument({ id: doc.id, name: doc.name, folderId: doc.folderId ?? null }, "library")}
                    >
                      <div className="w-4 flex-shrink-0" />
                      <Checkbox
                        checked={isDocSelected(doc.id) || selectAllLibrary}
                        className="pointer-events-none flex-shrink-0"
                      />
                      <FileTypeIcon mimeType={doc.mimeType} fileName={doc.name} size={20} className="flex-shrink-0" />
                      <span className="text-sm truncate min-w-0">{doc.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {filteredDocs.length === 0 && folders.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">No documents in library</p>
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Bid Documents Tab */}
          <TabsContent value="bids" className="mt-4 flex-1 min-h-0 flex flex-col">
            {/* One bid in Sources at a time */}
            <div className="flex items-center gap-2 px-2 py-2 border-b border-border mb-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Add one bid to search its tender documents.
              </span>
              {loadingBidDocs.size > 0 && (
                <span className="text-meta">(loading...)</span>
              )}
            </div>

            <ScrollArea className="flex-1 min-h-0 w-full">
              {filteredBids.map((bid) => {
                const isExpanded = expandedBids.has(bid.id)
                const docs = bidDocuments[bid.id] ?? []
                const isLoading = loadingBidDocs.has(bid.id)
                const wholeBid = isWholeBidGranted(bid.id)
                const hasSpecific = bidHasSpecificDocs(bid.id)
                const disabled = isBidDisabled(bid.id)

                return (
                  <div
                    key={bid.id}
                    className={cn(disabled && "opacity-50")}
                  >
                    {/* Bid row - styled like folder row */}
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer overflow-hidden">
                      <button
                        className="p-0.5 hover:bg-muted rounded flex-shrink-0"
                        aria-label={`Toggle ${bid.title} documents`}
                        onClick={() => toggleBid(bid.id)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                      <Checkbox
                        checked={wholeBid}
                        disabled={isLoading || disabled}
                        aria-label={`All documents in ${bid.title}`}
                        ref={(el) => {
                          if (el && hasSpecific) {
                            el.dataset["state"] = "indeterminate"
                          }
                        }}
                        onCheckedChange={(checked) => toggleWholeBid(bid.id, checked === true)}
                        className="pointer-events-auto flex-shrink-0"
                      />
                      <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm flex-1 truncate min-w-0">{bid.title}</span>
                      {isLoading ? (
                        <span className="text-meta flex-shrink-0">Loading...</span>
                      ) : (
                        <span className="text-meta flex-shrink-0">({docs.length})</span>
                      )}
                    </div>

                    {/* Whole-bid grant unlocks workspace reads (drafts, brief,
                        analysis) on the backend — disclose that here. Shown only
                        for a whole-bid grant, never for doc-pinned selections. */}
                    {wholeBid && (
                      <div className="pl-[5.25rem] pb-1 text-meta">
                        Includes workspace: response drafts, brief, analysis
                      </div>
                    )}

                    {/* Expanded bid documents */}
                    {isExpanded && (
                      <div className="ml-4 overflow-hidden">
                        {docs.length === 0 ? (
                          <div className="px-6 py-2 text-sm text-muted-foreground">
                            No documents uploaded for this bid
                          </div>
                        ) : (
                          docs.map((doc) => (
                            <button
                              key={doc.id}
                              disabled={disabled}
                              className={cn(
                                "w-full min-w-0 flex items-center gap-2 px-2 py-1.5 ml-4 rounded-md text-left transition-colors overflow-hidden",
                                isDocSelected(doc.id)
                                  ? "bg-primary/10 text-primary"
                                  : "hover:bg-accent",
                                disabled && "cursor-not-allowed"
                              )}
                              onClick={() => toggleBidDocument({ id: doc.id, name: doc.name }, bid.id)}
                            >
                              <div className="w-4 flex-shrink-0" />
                              <Checkbox
                                checked={isDocSelected(doc.id)}
                                disabled={disabled}
                                className="pointer-events-none flex-shrink-0"
                              />
                              <FileTypeIcon mimeType={doc.mimeType} fileName={doc.name} size={20} className="flex-shrink-0" />
                              <span className="text-sm truncate min-w-0">{doc.name}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )
              })}

              {bids.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <FolderOpen className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">No bids found</p>
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-shrink-0 flex items-center justify-between sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {getSelectionSummary()}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleApply}>
              Apply Context
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
